'use strict';

export default class VZAdvisoryCard extends HTMLElement {
    constructor() {
        super();

        this.shown = false;
        this.visibilityClass = 'show';
        this.waiting = 60_000;

        this.sidebar = this.getAttribute('sidebar');
        this.basket = this.getAttribute('basket');

        this.wrapper = this.closest('.js-advisory-card-wrapper');
    }

    static get observedAttributes() {
        return ['delay'];
    }

    attributeChangedCallback(attrName, oldVal, newVal) {
        if (attrName === 'delay' && oldVal !== newVal && this.shown === false) {
            this.waiting = parseFloat(newVal);
            this.showAdvisoryCard.bind(this)();
        }
    }

    /**
     * @private connectedCallback
     * @description
     * When entering the DOM, set a timeout to show
     * the advisory card after X seconds and adds a
     * DOM listener to the PLP sidebar events
     */
    connectedCallback() {
        this.timeout = this.showAdvisoryCard.bind(this)();

        document.addEventListener('plp:basketUpdated', this.hideAdvisoryCard.bind(this));
        this.querySelector('.js-dismiss').addEventListener('click', this.hideAdvisoryCard.bind(this));

        // Hide chat button if basket is empty on initial load
        this.constructor.toggleChatBubble(this.isEmptyBasket());
    }

    /**
     * @private disconnectedCallback
     * @description
     * Clears the timeout on DOM removal
     */
    disconnectedCallback() {
        clearTimeout(this.timeout);
    }

    /**
     * @public isEmptyBasket
     * @returns {boolean} - Whether basket is empty.
     * @description
     * Checks if the basket is empty.
     */
    isEmptyBasket() {
        const sidebar = document.querySelector(this.sidebar);
        const basket = JSON.parse(sidebar.getAttribute(this.basket));

        return basket.length === 0;
    }

    /**
     * @public showAdvisoryCard
     * @description
     * Waits for a timeout and then shows the advisory card if the basket is still empty
     */
    showAdvisoryCard() {
        this.timeout = setTimeout(() => {
            // Check if basket is empty
            if (!this.isEmptyBasket()) return;

            // Set the height of the card as a CSS variable
            const height = this.getBoundingClientRect().height;

            if (this.wrapper) this.wrapper.style.setProperty('--advisory-card-height', `${height}px`);

            // Show advisory card
            this.classList.add(this.visibilityClass);
            this.setAttribute('aria-hidden', 'false');
            this.shown = true;
        }, this.waiting);
    }

    /**
     * @public destroy
     * @description
     * If the element is not customer facing, it will
     * clear the timeout and remove the element from
     * the DOM.
     */
    destroy() {
        if (!this.classList.contains(this.visibilityClass)) {
            clearTimeout(this.timeout);

            this.parentElement?.parentElement?.removeChild(this.parentElement);

            // Show chat button when advisory card is closed
            this.constructor.toggleChatBubble(false);
        }
    }

    /**
     * @public hideAdvisoryCard
     * @description
     * Hides the advisory card
     */
    hideAdvisoryCard() {
        this.classList.remove(this.visibilityClass);
        this.setAttribute('aria-hidden', 'true');

        // Wait for the animation to be done before removing the element from the DOM.
        const transitionDuration = parseFloat(window.getComputedStyle(this).transitionDuration) * 1000;
        setTimeout(() => {
            this.destroy();
        }, transitionDuration);
    }

    /**
     * @private static toggleChatBubble
     * @param {boolean} value Toggle chat button display class
     * @description
     * Hides or shows the Dimelo chat bubble
     */
    static toggleChatBubble(value) {
        const chatBubble = document.querySelector('.dimelo_chat_item_markup');

        if (chatBubble) chatBubble.classList.toggle('d-none', value);
    }
}

if (!window.customElements.get('vz-advisory-card')) {
    window.customElements.define('vz-advisory-card', VZAdvisoryCard);
}
